import { useState } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Eye, EyeOff, Check, X } from 'lucide-react';
import { cn } from '@/lib/utils';

export const passwordRules = [
  { label: 'Mínimo de 8 caracteres', test: (v: string) => v.length >= 8 },
  { label: 'Uma letra maiúscula', test: (v: string) => /[A-Z]/.test(v) },
  { label: 'Uma letra minúscula', test: (v: string) => /[a-z]/.test(v) },
  { label: 'Um número', test: (v: string) => /\d/.test(v) },
  { label: 'Um caractere especial (!@#$...)', test: (v: string) => /[^A-Za-z0-9]/.test(v) },
];

export const passwordScore = (value: string) =>
  passwordRules.filter((r) => r.test(value)).length;

interface PasswordInputProps {
  id: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  required?: boolean;
  minLength?: number;
  autoComplete?: string;
  showStrength?: boolean;
  className?: string;
}

const PasswordInput = ({
  id,
  value,
  onChange,
  placeholder = '••••••••',
  required,
  minLength,
  autoComplete,
  showStrength = false,
  className,
}: PasswordInputProps) => {
  const [visible, setVisible] = useState(false);
  const score = passwordScore(value);

  const strengthLabel =
    score <= 2 ? 'Senha fraca' : score <= 3 ? 'Senha média' : score === 4 ? 'Senha forte' : 'Senha excelente';
  const strengthColor =
    score <= 2 ? 'bg-destructive' : score <= 3 ? 'bg-yellow-500' : score === 4 ? 'bg-primary' : 'bg-emerald-500';

  return (
    <div className="space-y-2">
      <div className="relative">
        <Input
          id={id}
          type={visible ? 'text' : 'password'}
          placeholder={placeholder}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          required={required}
          minLength={minLength}
          autoComplete={autoComplete}
          className={cn('bg-input border-border focus:border-primary pr-10', className)}
        />
        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label={visible ? 'Ocultar senha' : 'Mostrar senha'}
          className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
          onClick={() => setVisible(!visible)}
        >
          {visible ? (
            <EyeOff className="h-4 w-4 text-neutral-700" />
          ) : (
            <Eye className="h-4 w-4 text-neutral-700" />
          )}
        </Button>
      </div>

      {showStrength && value.length > 0 && (
        <div className="space-y-2">
          <div className="flex gap-1">
            {passwordRules.map((_, i) => (
              <div
                key={i}
                className={cn('h-1 flex-1 rounded-full', i < score ? strengthColor : 'bg-muted')}
              />
            ))}
          </div>
          <p className="text-xs font-medium text-muted-foreground">{strengthLabel}</p>
          <ul className="space-y-1">
            {passwordRules.map((rule) => {
              const ok = rule.test(value);
              return (
                <li
                  key={rule.label}
                  className={cn('flex items-center gap-2 text-xs', ok ? 'text-emerald-500' : 'text-muted-foreground')}
                >
                  {ok ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />}
                  {rule.label}
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
};

export default PasswordInput;
